import { useCallback, useEffect, useRef, useState } from "react";
import "../../../../DrugAssignmentBatchRelease/DrugAssignment.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Divider, Grid2 } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import SearchedComponent from "../../../../../components/SearchedComponent";
import Loader from "../../../../../components/Loader";
import StyledGridComp from "../../../../../components/StyledGridComp";
import {
  fetchDataForIndividualDrugStatus,
  fetchStatusLoadDataService,
  submitIndividualStatusDataService,
} from "../../../../../services/siteManagamentService";
import UncontrolledSearchSelect from "../../../../../components/UncontrolledSearchSelect";
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from "../../../../../utilities/helper";
import StudyKitIndividualConfirmPage from "./IndividualConfirmPage/StudyKitIndividualConfirmPage";

const IndividualKitStatus = () => {
  const [load, setLoad] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [rowData, setRowData] = useState([]);
  const [rowLength, setRowLength] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchedValue, setSearchedValue] = useState("");
  const [changedStatusSelection, setChangedStatusSelection] = useState({});
  const [loadData, setLoadData] = useState({
    changedStatusData: [],
    showSequenceNumberColumn: false,
  });
  const [locationName, setLocationName] = useState("");
  const [confirmationData, setConfirmData] = useState(null);
  const [showConfirmationPage, setShowConfirmationPage] = useState(false);

  const navigate = useNavigate();

  const access_token = localStorage?.getItem("access_token");
  const roleId = localStorage.getItem("roleId");
  const studyId = localStorage.getItem("studyId");
  const [hiddenColumns, setHiddenColumns] = useState([]);

  const siteId = useLocation()?.state?.id;
  const siteName = useLocation()?.state?.name;

  const gridWrapperRef = useRef(null);

  const { changedStatusData, showSequenceNumberColumn } = loadData;

  useEffect(() => {
    if (page >= 0 && pageSize && access_token && siteId && studyId) {
      fetchAllIndividualKitData(debouncedSearch, siteId, studyId);
      fetchLoadingData(siteId);
    }
  }, [debouncedSearch, siteId, studyId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchedValue);
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchedValue]);

  useEffect(() => {
    if (!showSequenceNumberColumn) {
      setHiddenColumns((prev) => [...prev, "sequenceNumber"]);
    }
  }, [showSequenceNumberColumn]);

  const columns = [
    {
      field: "kitNumber",
      headerName: "Drug Kit Number",
      width: 350,
      renderHeader: () => (
        <div className="grid-heading">{"Drug Kit Number"}</div>
      ),
      renderCell: (params) => {
        const drugNumber = params.row.kitNumber;

        return <span>{drugNumber}</span>;
      },
    },
    {
      field: "sequenceNumber",
      headerName: "Sequence Number",
      width: 350,
      renderHeader: () => (
        <>
          {showSequenceNumberColumn && (
            <div className="grid-heading">{"Sequence Number"}</div>
          )}
        </>
      ),
      renderCell: (params) => {
        const sequenceNumber = params.row.sequenceNumber;

        return <>{<span>{sequenceNumber}</span>}</>;
      },
    },
    {
      field: "currentStatus",
      headerName: "Current Status",
      width: 350,
      renderHeader: () => (
        <div className="grid-heading">{"Current Status"}</div>
      ),
      renderCell: (params) => {
        const currentStatus = params.row.currentStatus;

        return <span>{currentStatus}</span>;
      },
    },
    {
      field: "selectedStatus",
      headerName: "Change Status To",
      filterable: false,
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Change Status To"}</div>
      ),
      renderCell: (params) => {
        const rowId = params.id;
        const selectedStatus = params.row.selectedStatus;

        const value =
          rowId in changedStatusSelection
            ? changedStatusSelection[rowId]
            : selectedStatus || "";

        return (
          <UncontrolledSearchSelect
            scrollContainerRef={gridWrapperRef}
            data={changedStatusData}
            optionKey="id"
            optionLabel="name"
            value={value}
            onChange={(selectedValue) => {
              setChangedStatusSelection((prev) => ({
                ...prev,
                [rowId]: selectedValue,
              }));
            }}
          />
        );
      },
    },
  ];

  const fetchAllIndividualKitData = async (
    debouncedSearch,
    siteId,
    studyId
  ) => {
    setLoad(true);
    try {
      const res = await fetchDataForIndividualDrugStatus(
        siteId,
        1,
        100,
        studyId,
        roleId,
        access_token,
        debouncedSearch
      );
      if (res?.status === 200) {
        const data = res?.data?.result?.data?.map((item) => ({
          ...item,
          id: item?.drugId,
        }));
        setRowData(data);
        setRowLength(res?.data?.totalCount);
        setLoadData({
          changedStatusData: data[0]?.statuses || [],
          showSequenceNumberColumn: res?.data?.result?.isUnblindedUser,
        });
        setLoad(false);
      } else if (res?.status == 401) {
        navigate("/unauthorize");
      }
    } catch (err) {
      setLoad(false);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const fetchLoadingData = async (id) => {
    try {
      const res = await fetchStatusLoadDataService(access_token, id, roleId);
      if (res?.status == 200) {
        setLocationName(res?.data?.result?.locationName);
      }
    } catch (err) {
      console.error("Error fetching loading data:", err);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const handleSearch = useCallback((e) => {
    setSearchedValue(e.target.value);
  }, []);

  const submitData = async () => {
    try {
      setLoad(true);

      const json = rowData
        ?.filter((item) => changedStatusSelection.hasOwnProperty(item?.drugId))
        .map((item) => ({
          drugId: item?.drugId,
          siteName: locationName,
          kitNumber: item?.kitNumber,
          currentStatus: item?.currentStatus,
          currentStatusId: item?.selectedStatus,
          selectedStatus: changedStatusSelection[item?.drugId],
          sequenceNumber: item?.sequenceNumber || null,
          locationName: locationName,
          statuses: null,
        }));

      const payload = {
        drugs: null,
        errorMessage: "",
        drugUpdateJson: JSON.stringify(json),
        id: siteId,
        locationTypeId: 0,
        locationName: locationName,
        isUnblindedUser: showSequenceNumberColumn,
      };

      const res = await submitIndividualStatusDataService(
        access_token,
        roleId,
        payload
      );

      if (
        res?.status === 200 &&
        res?.data?.message === "Updated Successfully!"
      ) {
        notifySuccess("Status Updated Successfully !");
        setShowConfirmationPage(true);
        setConfirmData(res?.data?.result);
      } else if (
        res?.status === 200 &&
        res?.data?.message === "No updates selected"
      ) {
        notifyWarning("No Changed Made !");
      } else if (
        res?.status == 200 &&
        res?.data?.message?.includes(
          "Error converting value \"\" to type 'System.Guid'"
        )
      ) {
        notifyWarning("Please Select a valid status.");
      }
    } catch (err) {
      console.log("Error", err?.message);
      notifyError(err?.message);
      if (err?.status === 401) {
        navigate("/unAuthorize");
      }
    } finally {
      setLoad(false);
    }
  };

  return (
    <>
      {showConfirmationPage ? (
        <StudyKitIndividualConfirmPage
          data={confirmationData}
          setShowConfirm={setShowConfirmationPage}
          siteId={siteId}
          siteName={siteName}
        />
      ) : (
        <>
          <div className="content-body">
            <p
              className="bread-crumbs-link-container"
              style={{ fontWeight: "600" }}
            >
              <Link className="bread-crumbs-link" to="/">
                Home
              </Link>{" "}
              |{" "}
              <Link className="bread-crumbs-link" to="/site-management">
                Site Management
              </Link>{" "}
              |
              <button
                className="bread-crumbs-btn"
                onClick={(e) =>
                  navigate("/addUpdate-site-management", {
                    state: { id: siteId },
                  })
                }
              >
                {siteName || "Site Details"}
              </button>
              |{" "}
              <button
                className="bread-crumbs-btn"
                onClick={(e) =>
                  navigate("/individual-kit-status", {
                    state: { id: siteId, name: siteName },
                  })
                }
              >
                Individual Drug Kit Status Management
              </button>
              -{" "}
              <b style={{ color: "gray" }}>
                {localStorage?.getItem("studyName")}
              </b>
            </p>
            <Grid2
              container
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%", margin: "0 auto" }}
            >
              <Grid2 item md={6} sx={{ width: "100%" }}>
                <p className="page-heading">
                  Individual Drug Kit Status Managament
                </p>
                <Divider />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "20px",
                    alignItems: "center",
                  }}
                >
                  <p className="page-sub-header">
                    <InfoOutlined className="info-icon" />
                    if you want to make an update to a large number of
                    kits/units, please use the
                    <button
                      className="bread-crumbs-btn"
                      onClick={(e) =>
                        navigate("/bulk-kit-status", {
                          state: {
                            siteName: siteName,
                            siteId: siteId,
                          },
                        })
                      }
                      style={{ fontSize: "14px" }}
                    >
                      Bulk update function
                    </button>
                  </p>
                </div>
              </Grid2>
            </Grid2>

            <div
              className="search-bar-container"
              style={{
                display: "flex",
                justifyContent: "end",
                margin: "-1% 1% 1% 1%",
              }}
            >
              <SearchedComponent
                handleSearch={handleSearch}
                placeholder="Batches"
                searchedValue={searchedValue}
              />
            </div>

            {load ? (
              <Loader />
            ) : (
              <StyledGridComp
                rows={rowData}
                columns={columns}
                page={page}
                hidden={hiddenColumns}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                isPaginationClientSide={true}
                length={rowLength}
              ></StyledGridComp>
            )}

            <Grid2
              alignItems={"end"}
              justifyContent="end"
              container
              gap={3}
              sx={{ marginTop: "2%" }}
            >
              <Grid2 item md={6}>
                <button
                  type="button"
                  className="create-btn-outlined"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/addUpdate-site-management");
                  }}
                >
                  Back
                </button>
              </Grid2>
              <Grid2 item md={6}>
                <button onClick={submitData} className="create-btn">
                  Save
                </button>
              </Grid2>
            </Grid2>
          </div>
        </>
      )}
    </>
  );
};

export default IndividualKitStatus;
